<template>
  <section class="add-member-page py-6 px-4">
    <b-container fluid>
      <b-card class="border-0 shadow-sm">
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="h4 text-primary mb-1">Add New Member</h2>
              <p class="mb-0 text-muted">
                Create a new member for your agent plan
              </p>
            </div>
            <b-button variant="outline-secondary" @click="goBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Back to Dashboard
            </b-button>
          </div>
        </template>

        <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
          <b-form @submit.prevent="handleSubmit(onSubmit)" @reset.prevent="resetForm">
            <!-- Personal Information Section -->
            <b-card class="mb-3 border-0 shadow-sm" no-body>
              <b-card-header class="bg-light border-bottom">
                <h6 class="mb-0 text-primary">
                  <i class="fas fa-user mr-2"></i>
                  Personal Information
                </h6>
              </b-card-header>
              <b-card-body>
                <b-row>
                  <b-col cols="12" md="6">
                    <validation-provider #default="validationContext" name="name" rules="required">
                      <b-form-group label="First Name" label-for="name">
                        <b-form-input 
                          id="name" 
                          v-model="stateData.name" 
                          autofocus
                          :state="getValidationState(validationContext)" 
                          trim
                          placeholder="Enter first name" />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col cols="12" md="6">
                    <validation-provider #default="validationContext" name="lastname" rules="required">
                      <b-form-group label="Last Name" label-for="lastname">
                        <b-form-input 
                          id="lastname" 
                          v-model="stateData.lastname" 
                          :state="getValidationState(validationContext)" 
                          trim
                          placeholder="Enter last name" />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col cols="12" md="6">
                    <validation-provider #default="validationContext" name="date_birth" rules="required">
                      <b-form-group label="Date of Birth" label-for="date_birth">
                        <flat-pickr 
                          id="date_birth" 
                          v-model="stateData.date_birth"
                          :config="dateConfig"
                          class="form-control"
                          :class="{ 'is-invalid': getValidationState(validationContext) === false }"
                          placeholder="Select date of birth" />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col cols="12" md="6">
                    <validation-provider #default="validationContext" name="gender" rules="required">
                      <b-form-group label="Gender" label-for="gender">
                        <treeselect 
                          id="gender" 
                          v-model="stateData.gender"
                          :options="genderOptions" 
                          :normalizer="normalizer"
                          :clearable="false"
                          placeholder="Select gender" />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col cols="12" md="6">
                    <validation-provider #default="validationContext" name="email" rules="required|email">
                      <b-form-group label="Email" label-for="email">
                        <b-form-input 
                          id="email" 
                          v-model="stateData.email" 
                          type="email"
                          :state="getValidationState(validationContext)" 
                          trim
                          placeholder="Enter email address" />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col cols="12" md="6">
                    <validation-provider #default="validationContext" name="phone" rules="required">
                      <b-form-group label="Phone" label-for="phone">
                        <b-form-input 
                          id="phone" 
                          v-model="stateData.phone" 
                          :state="getValidationState(validationContext)" 
                          trim
                          placeholder="Enter phone number" />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- Membership Information Section -->
            <b-card class="mb-3 border-0 shadow-sm" no-body>
              <b-card-header class="bg-light border-bottom">
                <h6 class="mb-0 text-primary">
                  <i class="fas fa-id-card mr-2"></i>
                  Membership Information
                </h6>
              </b-card-header>
              <b-card-body>
                <b-row>
                  <b-col cols="12" md="6">
                    <validation-provider #default="validationContext" name="user_references_id" rules="required">
                      <b-form-group label="Reference" label-for="user_references_id">
                        <treeselect 
                          id="user_references_id" 
                          v-model="stateData.user_references_id"
                          :options="referenceOptions" 
                          :normalizer="normalizer"
                          :clearable="false"
                          placeholder="Select reference" />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col cols="12" md="6">
                    <validation-provider #default="validationContext" name="memberships_parameters_id" rules="required">
                      <b-form-group label="Membership Plan" label-for="memberships_parameters_id">
                        <treeselect 
                          id="memberships_parameters_id" 
                          v-model="stateData.parameter"
                          :async="true"
                          :load-options="searchParametersAll"
                          :defaultOptions="true"
                          :clearable="false"
                          :normalizer="normalizerMembership"
                          valueFormat="object"
                          placeholder="Select membership plan" />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                        <small v-if="stateData.parameter && stateData.parameter.price" class="text-muted mt-1 d-block">
                          Plan Price: {{ stateData.parameter.price }}
                        </small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- Payment Information Section -->
            <b-card class="mb-3 border-0 shadow-sm" no-body>
              <b-card-header class="bg-light border-bottom">
                <h6 class="mb-0 text-primary">
                  <i class="fas fa-money-bill-wave mr-2"></i>
                  Payment Information
                </h6>
              </b-card-header>
              <b-card-body>
                <b-row>
                  <b-col cols="12" md="6">
                    <validation-provider 
                      #default="validationContext" 
                      name="cash" 
                      :rules="{
                        required: true,
                        numeric: true,
                        min_value: 0,
                        totalRules: {
                          total: maxPrice,
                          totalPay: splitPay,
                        },
                      }">
                      <b-form-group label="Cash Payment" label-for="cash">
                        <b-form-input 
                          id="cash" 
                          v-model.number="stateData.cash" 
                          type="number"
                          min="0"
                          step="0.01"
                          :state="getValidationState(validationContext)" 
                          trim
                          placeholder="Enter cash amount" />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] || validateTotalMessage }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col cols="12" md="6">
                    <validation-provider 
                      #default="validationContext" 
                      name="cc" 
                      :rules="{
                        required: true,
                        numeric: true,
                        min_value: 0,
                        totalRules: {
                          total: maxPrice,
                          totalPay: splitPay,
                        },
                      }">
                      <b-form-group label="Credit Card Payment" label-for="cc">
                        <b-form-input 
                          id="cc" 
                          v-model.number="stateData.cc" 
                          type="number"
                          min="0"
                          step="0.01"
                          :state="getValidationState(validationContext)" 
                          trim
                          placeholder="Enter credit card amount" />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] || validateTotalMessage }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <b-alert 
                  v-if="stateData.parameter && stateData.parameter.price" 
                  variant="info" 
                  :show="true"
                  class="mt-3">
                  <strong>Total Plan Price:</strong> {{ stateData.parameter.price }}<br>
                  <strong>Cash:</strong> {{ stateData.cash || 0 }}<br>
                  <strong>Credit Card:</strong> {{ stateData.cc || 0 }}<br>
                  <strong>Total Payment:</strong> {{ splitPay }}<br>
                  <strong>Remaining:</strong> {{ (stateData.parameter.price - splitPay).toFixed(2) }}
                </b-alert>
              </b-card-body>
            </b-card>

            <!-- Form Actions -->
            <div class="d-flex justify-content-end mt-4 pt-3 border-top">
              <b-button 
                type="button" 
                variant="outline-secondary" 
                class="mr-2"
                @click="resetForm"
                :disabled="loading">
                <i class="fas fa-redo mr-1"></i>
                Reset
              </b-button>
              <b-button 
                variant="primary" 
                type="submit" 
                :disabled="loading">
                <span v-if="!loading">
                  <i class="fas fa-save mr-1"></i>
                  Create Member
                </span>
                <span v-else>
                  <SpinnerLoading />
                  Creating...
                </span>
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-card>

      <!-- Success/Error Alerts -->
      <b-alert
        v-if="successMessage"
        variant="success"
        :show="Boolean(successMessage)"
        dismissible
        @dismissed="successMessage = null"
        class="mt-3">
        <i class="fas fa-check-circle mr-2"></i>
        {{ successMessage }}
      </b-alert>

      <b-alert
        v-if="errorMessage"
        variant="danger"
        :show="Boolean(errorMessage)"
        dismissible
        @dismissed="errorMessage = null"
        class="mt-3">
        <i class="fas fa-exclamation-circle mr-2"></i>
        {{ errorMessage }}
      </b-alert>
    </b-container>
  </section>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BContainer,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BRow,
  BCol,
  BAlert,
} from 'bootstrap-vue'
import formValidation from '@/core/utils/forms/form-validation'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import SpinnerLoading from '@/components/SpinnerLoading'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { required } from '@validations'
import { ref, computed, watch, onMounted } from '@vue/composition-api'
import agentPlanService from '@/core/services/api/agentPlan'
import axiosCT from '@/core/services/api/admin/consults/memberships'
import axiosP from '@/core/services/api/patient'

/**
 * Component for adding new members to agent plan.
 * Allows agents to register new members with personal information, membership selection and payment details.
 */
export default {
  name: 'AddMember',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BContainer,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BRow,
    BCol,
    BAlert,
    SpinnerLoading,
    flatPickr,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      required,
      genderOptions: [
        { name: 'Male', id: 'male' },
        { name: 'Female', id: 'female' },
      ],
      normalizer(node) {
        return {
          id: node.id,
          label: node.name || node.label,
        }
      },
      normalizerMembership(node) {
        return {
          id: node.id,
          label: node.name || `${node.name} - ${node.price}`,
        }
      },
      dateConfig: {
        dateFormat: 'm-d-Y',
        allowInput: true,
        static: true,
      },
    }
  },
  setup() {
    const stateDataTem = {
      name: null,
      lastname: null,
      date_birth: null,
      phone: null,
      gender: null,
      email: null,
      user_references_id: null,
      memberships_parameters_id: null,
      parameter: null,
      cash: 0,
      cc: 0,
    }

    const stateData = ref(JSON.parse(JSON.stringify(stateDataTem)))
    const loading = ref(false)
    const successMessage = ref(null)
    const errorMessage = ref(null)
    const referenceOptions = ref([])
    const maxPrice = ref(0)
    const userData = JSON.parse(localStorage.getItem('userData') || '{}')

    /**
     * Loads reference options from the API
     */
    const loadReferences = () => {
      axiosP
        .referencePagination(100, 1)
        .then(({ data }) => {
          referenceOptions.value = (data.data || []).map(reference => ({
            id: reference.id,
            name: reference.name,
          }))
        })
        .catch(error => {
          console.error('Error loading references:', error)
          referenceOptions.value = []
        })
    }

    /**
     * Searches membership parameters asynchronously for treeselect
     */
    const searchParameters = ({ action, searchQuery, callback }) => {
      if (action === 'ASYNC_SEARCH') {
        const datos = {
          name: searchQuery,
        }
        axiosCT
          .membershipParameterFilter(10, datos)
          .then(({ data: { data } }) => {
            const formatted = data.map(param => ({
              id: param.id,
              name: `${param.name} - ${param.price}`,
              price: param.price,
            }))
            callback(null, formatted)
          })
          .catch(error => {
            console.error('Error loading membership parameters:', error)
            callback(error)
          })
      }
    }

    /**
     * Loads all membership plans from /api/get-plans/all endpoint
     */
    const searchParametersAll = ({ action, searchQuery, callback }) => {
      if (action === 'ASYNC_SEARCH') {
        axiosCT
          .getAllPlans()
          .then((response) => {
            // El servicio ya devuelve res.data, así que response puede ser directamente el array
            // o puede estar dentro de response.data dependiendo de la estructura del endpoint
            let data = response
            if (response && response.data) {
              data = response.data
            }
            
            // Asegurar que data sea un array
            let filteredData = Array.isArray(data) ? data : []
            
            // Filtrar por búsqueda si hay searchQuery
            if (searchQuery && searchQuery.trim()) {
              const query = searchQuery.toLowerCase()
              filteredData = filteredData.filter(param => 
                param.name?.toLowerCase().includes(query)
              )
            }
            
            const formatted = filteredData.map(param => ({
              id: param.id,
              name: `${param.name} - ${param.price}`,
              price: param.price,
            }))
            callback(null, formatted)
          })
          .catch(error => {
            console.error('Error loading all plans:', error)
            callback(error)
          })
      }
    }

    /**
     * Calculates total payment split between cash and credit card
     */
    const splitPay = computed(() => {
      const cash = parseFloat(stateData.value.cash) || 0
      const cc = parseFloat(stateData.value.cc) || 0
      return cash + cc
    })

    /**
     * Validation message for total payment
     */
    const validateTotalMessage = computed(() => {
      if (!stateData.value.parameter || !stateData.value.parameter.price) {
        return ''
      }
      if (splitPay.value !== stateData.value.parameter.price) {
        return `Total payment (${splitPay.value}) must equal plan price (${stateData.value.parameter.price})`
      }
      return ''
    })

    /**
     * Custom validation rule for total payment
     */
    extend('totalRules', {
      params: ['total', 'totalPay'],
      validate: (value, { total, totalPay }) => {
        if (!total || totalPay == null) return true
        return totalPay === total
      },
      message: (field, { total }) =>
        `Total payment must equal plan price of ${total}`,
    })

    /**
     * Watches for parameter changes to update max price
     */
    watch(
      () => stateData.value.parameter,
      (newVal) => {
        if (!newVal) {
          maxPrice.value = 0
          return
        }
        maxPrice.value = newVal.price || 0
      },
      { deep: true }
    )

    /**
     * Resets form data
     */
    const resetuserData = () => {
      stateData.value = JSON.parse(JSON.stringify(stateDataTem))
      successMessage.value = null
      errorMessage.value = null
    }

    /**
     * Formats date from MM-DD-YYYY to backend format
     */
    const formatDateForBackend = (dateString) => {
      if (!dateString) return null
      // dateString is in format MM-DD-YYYY
      return dateString
    }

    /**
     * Handles form submission
     */
    const onSubmit = () => {
      loading.value = true
      errorMessage.value = null
      successMessage.value = null

      try {
        // Prepare form data
        const formData = {
          name: stateData.value.name,
          lastname: stateData.value.lastname,
          date_birth: formatDateForBackend(stateData.value.date_birth),
          phone: stateData.value.phone,
          gender: stateData.value.gender,
          email: stateData.value.email,
          user_references_id: stateData.value.user_references_id,
          agent_user_id: userData?.id || null,
          memberships_parameters_id: stateData.value.parameter?.id || stateData.value.memberships_parameters_id,
          cash: parseFloat(stateData.value.cash) || 0,
          cc: parseFloat(stateData.value.cc) || 0,
        }

        // Validate agent_user_id
        if (!formData.agent_user_id) {
          throw new Error('Agent user ID not found. Please login again.')
        }

        // Validate payment totals
        if (stateData.value.parameter && stateData.value.parameter.price) {
          if (splitPay.value !== stateData.value.parameter.price) {
            errorMessage.value = `Total payment (${splitPay.value}) must equal plan price (${stateData.value.parameter.price})`
            loading.value = false
            return
          }
        }

        agentPlanService
          .createAgentMember(formData)
          .then((response) => {
            loading.value = false
            successMessage.value = response.message || 'Member created successfully!'
            resetuserData()
            
            // Redirect after 2 seconds
            setTimeout(() => {
              window.location.href = '/agent-plan-dashboard'
            }, 2000)
          })
          .catch((error) => {
            loading.value = false
            const errorMsg = error.response?.data?.message || error.message || 'Error creating member. Please try again.'
            errorMessage.value = errorMsg
            console.error('Error creating member:', error)
          })
      } catch (error) {
        loading.value = false
        errorMessage.value = error.message || 'Error processing form. Please check all fields.'
        console.error('Form submission error:', error)
      }
    }

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetuserData)

    onMounted(() => {
      loadReferences()
    })

    return {
      stateData,
      loading,
      successMessage,
      errorMessage,
      referenceOptions,
      maxPrice,
      splitPay,
      validateTotalMessage,
      searchParameters,
      searchParametersAll,
      loadReferences,
      onSubmit,
      refFormObserver,
      getValidationState,
      resetForm,
    }
  },
  methods: {
    goBack() {
      this.$router.push('/agent-plan-dashboard')
    },
  },
}
</script>

<style lang="scss" scoped>
.add-member-page {
  .card {
    border-radius: 0.5rem;
  }

  .bg-light {
    background-color: #f8f9fa !important;
  }

  .shadow-sm {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
  }
}
</style>
